import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "../shared/Icon";
import { connect } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import {
  fetchAllOrders,
  isAbortError,
  MANAGER_STATES,
  isClosedState,
} from "../../../services/managerApi";
import { filterOrders, pageCount, pageSlice } from "./filterOrders";
import { openReceipts } from "../shared/receipts";
import useOrderMetrics from "../../../hooks/useOrderMetrics";
import OrdersTable from "./OrdersTable";
import OrderDrawer from "../OrderDrawer";
import { ErrorState } from "../shared/States";
import { stateLabel } from "../shared/StatusPill";
import { humanizePaymentStatus } from "../shared/PaymentPill";
import { formatCurrency, formatKilos } from "../shared/format";

const TABS = [
  { key: "open", label: "Lopend" },
  { key: "done", label: "Afgerond" },
  { key: "all", label: "Alles" },
];

const SEARCH_DEBOUNCE_MS = 350;

const readQuery = (search) => {
  const params = new URLSearchParams(search);
  return {
    q: params.get("q") || "",
    state: params.get("state") || "",
    pickupDate: params.get("pickup_date") || "",
  };
};

const Bestellingen = ({ t, authUser, location, history }) => {
  const initial = useMemo(() => readQuery(location.search), [location.search]);

  const [tab, setTab] = useState(isClosedState(initial.state) ? "done" : "open");
  const [search, setSearch] = useState(initial.q);
  const [debouncedSearch, setDebouncedSearch] = useState(initial.q);
  const [stateFilter, setStateFilter] = useState(initial.state);
  const [pickupDate, setPickupDate] = useState(initial.pickupDate);

  const [orders, setOrders] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);
  const [sort, setSort] = useState({ key: "pickupDate", direction: "desc" });
  const [openOrder, setOpenOrder] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [receiptError, setReceiptError] = useState(null);

  const abortRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, stateFilter, pickupDate, tab, sort]);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let active = true;

    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Tout est chargé d'un coup, puis filtré, trié et paginé ici.
        // Auparavant le serveur paginait par 25 et l'écran filtrait ensuite par
        // onglet : sur les 47 476 commandes de production, `delivery` en couvre
        // 45 594, donc une page de 25 ne contenait qu'une ou deux commandes
        // ouvertes. L'onglet « Lopend » affichait deux lignes en annonçant
        // « 1 / 12 », et les pages suivantes paraissaient vides.
        const result = await fetchAllOrders({
          authUser,
          pickupDate: pickupDate || undefined,
          signal: controller.signal,
        });
        if (!active) return;
        setOrders(result.orders);
        setTruncated(result.truncated);
      } catch (err) {
        if (!active || isAbortError(err)) return;
        setError(err);
        setOrders([]);
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [authUser, pickupDate, reloadKey]);

  // Filtrer et trier d'abord, découper ensuite — voir `filterOrders.js`.
  const matchingOrders = useMemo(
    () => filterOrders({ orders, search: debouncedSearch, tab, state: stateFilter, sort }),
    [orders, debouncedSearch, tab, stateFilter, sort]
  );

  /**
   * L'écran vide doit dire ce qui est vide.
   *
   * Le message parlait de filtres à ajuster dans tous les cas — y compris sur
   * l'onglet « En cours » ouvert par défaut, où le manager n'a rien filtré du
   * tout. Il cherchait alors un réglage inexistant au lieu de lire l'essentiel :
   * rien n'est en cours en ce moment.
   */
  const emptyState = useMemo(() => {
    const filtered = Boolean(debouncedSearch.trim() || stateFilter || pickupDate);
    if (filtered) {
      return {
        title: t("Geen bestellingen gevonden"),
        hint: t("Pas de filters aan of wis ze om alles te zien."),
      };
    }
    if (tab === "open") {
      return {
        title: t("Geen lopende bestellingen"),
        hint: t("Alles is terug. Kijk bij « Afgerond » voor de geleverde bestellingen."),
      };
    }
    if (tab === "done") {
      return {
        title: t("Nog geen afgeronde bestellingen"),
        hint: t("Kijk bij « Lopend » voor wat onderweg is."),
      };
    }
    return {
      title: t("Geen bestellingen gevonden"),
      hint: t("Dit verzamelpunt heeft nog geen bestellingen."),
    };
  }, [debouncedSearch, stateFilter, pickupDate, tab, t]);

  const totalPages = pageCount(matchingOrders.length);

  // La page courante, seule portion réellement affichée.
  const visibleOrders = useMemo(() => pageSlice(matchingOrders, page), [matchingOrders, page]);

  // Les kilos et les sacs ne sont pas dans la liste : on les complète pour les
  // lignes affichées seulement (voir useOrderMetrics).
  const metricsFor = useOrderMetrics(visibleOrders, authUser);

  // Pas de somme d'argent ici : le pied de tableau ne totalise plus que ce
  // qu'il a réellement sous les yeux, la page courante.
  const totals = useMemo(
    () => ({
      count: matchingOrders.length,
      kilos: matchingOrders.reduce((sum, order) => sum + (order.kilos || 0), 0),
    }),
    [matchingOrders]
  );

  const selectedOrders = useMemo(
    () => visibleOrders.filter((order) => selectedIds.includes(order.id)),
    [visibleOrders, selectedIds]
  );

  const toggle = useCallback((id) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id]));
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((ids) => {
      const visibleIds = visibleOrders.map((order) => order.id);
      const allOn = visibleIds.every((id) => ids.includes(id));
      return allOn ? ids.filter((id) => !visibleIds.includes(id)) : [...new Set([...ids, ...visibleIds])];
    });
  }, [visibleOrders]);

  const onSort = useCallback((key) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  }, []);

  const clearFilters = () => {
    setSearch("");
    setStateFilter("");
    setPickupDate("");
    history.replace(location.pathname);
  };

  const exportCsv = () => {
    const rows = [
      ["Nr.", "Bewoner", "Kamer", "Opgehaald", "Terug verwacht", "Kg", "Zakken", "Prijs", "Status", "Betaling"],
      ...selectedOrders.map((order) => [
        order.reference,
        order.residentName,
        order.room ?? "",
        order.pickupDate ? order.pickupDate.toISOString().slice(0, 10) : "",
        order.deliveryDate ? order.deliveryDate.toISOString().slice(0, 10) : "",
        order.kilos ?? "",
        order.bags ?? "",
        order.total ?? "",
        stateLabel(order.state, t),
        humanizePaymentStatus(order.paymentStatus) || "",
      ]),
    ];

    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
      .join("\n");

    const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `wosh-bestellingen-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadReceipts = async (ids) => {
    setBulkBusy(true);
    setReceiptError(null);
    const result = await openReceipts({ authUser, orderIds: ids });
    if (!result.ok) setReceiptError({ reason: result.reason, detail: result.detail });
    setBulkBusy(false);
  };

  const drawerIndex = openOrder
    ? visibleOrders.findIndex((order) => order.id === openOrder.id)
    : -1;

  return (
    <>
      <div className="mgr-head">
        <h1 className="mgr-h1">{t("Bestellingen")}</h1>
        <div className="mgr-search-sm">
          <Icon name="search" style={{ fontSize: 19, color: "var(--mgr-brand)" }} />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("Zoek bewoner, kamer of nummer")}
            aria-label={t("Zoek bewoner, kamer of nummer")}
          />
        </div>
      </div>

      <div className="mgr-tabs">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`mgr-tab${tab === item.key ? " is-active" : ""}`}
            onClick={() => setTab(item.key)}
          >
            {t(item.label)}
          </button>
        ))}
      </div>

      <div className="mgr-filters">
        <span style={{ fontSize: 14, fontWeight: 700, color: "var(--mgr-ink-2)" }}>
          {t("Filters")}:
        </span>

        {pickupDate ? (
          <button type="button" className="mgr-chip" onClick={() => setPickupDate("")}>
            {t("Opgehaald")}: {pickupDate}
            <Icon name="close" style={{ fontSize: 15 }} />
          </button>
        ) : null}

        <select
          className="mgr-select"
          value={stateFilter}
          onChange={(event) => setStateFilter(event.target.value)}
          aria-label={t("Status")}
        >
          <option value="">{t("Status")}: {t("alle")}</option>
          {MANAGER_STATES.map((state) => (
            <option key={state} value={state}>
              {stateLabel(state, t)}
            </option>
          ))}
        </select>

        {search || stateFilter || pickupDate ? (
          <button
            type="button"
            className="mgr-link"
            style={{ textDecoration: "underline" }}
            onClick={clearFilters}
          >
            {t("Filters wissen")}
          </button>
        ) : null}

        <div
          style={{
            marginLeft: "auto",
            fontSize: 14.5,
            fontWeight: 700,
            color: "var(--mgr-ink-2)",
          }}
        >
          {totals.count} {t("resultaten")}
          {totals.kilos > 0 ? ` · ${formatKilos(totals.kilos)} kg` : ""}
        </div>
      </div>

      {error ? (
        <ErrorState
          message={t("De gegevens konden niet geladen worden.")}
          retryLabel={t("Opnieuw proberen")}
          onRetry={() => setReloadKey((key) => key + 1)}
        />
      ) : null}

      {receiptError ? (
        <ErrorState
          message={
            receiptError.reason === "no-url"
              ? t("De server heeft geen bon teruggegeven voor deze selectie.")
              : `${t("De bonnen konden niet gedownload worden.")}${
                  receiptError.detail ? ` (${receiptError.detail})` : ""
                }`
          }
        />
      ) : null}

      {selectedOrders.length > 0 ? (
        <div className="mgr-selbar">
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <Icon name="checkbox" style={{ fontSize: 22 }} />
            <span style={{ fontSize: 15.5, fontWeight: 700 }}>
              {selectedOrders.length} {t("bestellingen geselecteerd")} ·{" "}
              {formatCurrency(selectedOrders.reduce((sum, order) => sum + (order.total || 0), 0))}
            </span>
            <button
              type="button"
              className="mgr-link"
              style={{ color: "rgba(255,255,255,.75)", textDecoration: "underline" }}
              onClick={() => setSelectedIds([])}
            >
              {t("Selectie wissen")}
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="mgr-selbar-btn" onClick={exportCsv}>
              <Icon name="download-outline" style={{ fontSize: 17 }} />
              {t("CSV exporteren")}
            </button>
            <button
              type="button"
              className="mgr-selbar-btn is-primary"
              disabled={bulkBusy}
              onClick={() => downloadReceipts(selectedOrders.map((order) => order.id))}
            >
              <Icon name="folder-outline" style={{ fontSize: 17 }} />
              {bulkBusy ? t("Bezig…") : t("Bonnen downloaden (ZIP)")}
            </button>
          </div>
        </div>
      ) : null}

      <OrdersTable
        orders={visibleOrders}
        emptyTitle={emptyState.title}
        emptyHint={emptyState.hint}
        isLoading={isLoading}
        selectedIds={selectedIds}
        onToggle={toggle}
        onToggleAll={toggleAll}
        onOpen={setOpenOrder}
        onDownloadReceipt={(order) => downloadReceipts([order.id])}
        sort={sort}
        onSort={onSort}
        totals={totals}
        metricsFor={metricsFor}
        t={t}
      />

      {/* Le plafond de chargement se voit : 13 des 82 sociétés gérées dépassent
          800 commandes sur douze mois, et sans ce mot leurs filtres porteraient
          silencieusement sur un sous-ensemble. */}
      {truncated ? (
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--mgr-ink-3)" }}>
          {t(
            "Cijfers berekend op de recentste bestellingen. Volledige totalen volgen zodra de server ze levert."
          )}
        </div>
      ) : null}

      {totalPages > 1 ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
          <button
            type="button"
            className="mgr-btn-ghost"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            {t("Previous")}
          </button>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--mgr-ink-2)" }}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="mgr-btn-ghost"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          >
            {t("Next")}
          </button>
        </div>
      ) : null}

      {openOrder ? (
        <OrderDrawer
          order={openOrder}
          onClose={() => setOpenOrder(null)}
          onPrev={
            drawerIndex > 0 ? () => setOpenOrder(visibleOrders[drawerIndex - 1]) : undefined
          }
          onNext={
            drawerIndex >= 0 && drawerIndex < visibleOrders.length - 1
              ? () => setOpenOrder(visibleOrders[drawerIndex + 1])
              : undefined
          }
          onDownloadReceipt={() => downloadReceipts([openOrder.id])}
        />
      ) : null}
    </>
  );
};

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default compose(
  withRouter,
  withTranslation(),
  connect(mapStateToProps)
)(Bestellingen);
